import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronUp, Mail, Sparkles, Loader2, Target } from 'lucide-react';

const SIGNAL_LABELS = {
  narrative: 'Narrative',
  direction: 'Direction',
  initiative: 'Initiative',
  depth: 'Depth',
  voice: 'Voice',
  differentiation: 'Differentiation',
};

function recBadge(rec) {
  if ((rec || '').includes('Elite')) return <Badge className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37] text-[10px]">Elite Band</Badge>;
  if ((rec || '').includes('Strategy')) return <Badge className="bg-[#0F392B] text-white hover:bg-[#0F392B] text-[10px]">Strategy Call</Badge>;
  return <Badge variant="outline" className="text-[10px]">Platform</Badge>;
}

function SubmissionRow({ sub }) {
  const [open, setOpen] = useState(false);
  const date = sub.created_at ? new Date(sub.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  return (
    <>
      <tr className="border-b border-[#E2E2E2] hover:bg-[#F9F9F7] cursor-pointer" onClick={() => setOpen(!open)} data-testid={`diagnostic-lead-row-${sub.id}`}>
        <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">{date}</td>
        <td className="py-3 px-4 text-sm">
          {sub.email ? (
            <span className="flex items-center gap-1.5 font-medium text-[#0F392B]"><Mail className="h-3 w-3 text-[#D4AF37]" />{sub.email}</span>
          ) : (
            <span className="text-muted-foreground text-xs">No email</span>
          )}
        </td>
        <td className="py-3 px-4">
          <span className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{sub.total}<span className="text-xs text-muted-foreground font-normal">/36</span></span>
        </td>
        <td className="py-3 px-4">{recBadge(sub.recommendation)}</td>
        <td className="py-3 px-4 text-xs text-muted-foreground">
          {sub.ai_analysis ? <span className="flex items-center gap-1"><Sparkles className="h-3 w-3 text-[#D4AF37]" /> Yes</span> : '—'}
        </td>
        <td className="py-3 px-4 text-right">
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground inline" /> : <ChevronDown className="h-4 w-4 text-muted-foreground inline" />}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-[#E2E2E2] bg-[#F9F9F7]" data-testid={`diagnostic-lead-detail-${sub.id}`}>
          <td colSpan={6} className="py-4 px-6">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
              {Object.entries(SIGNAL_LABELS).map(([k, label]) => (
                <div key={k} className="bg-white border border-[#E2E2E2] rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                  <p className={`text-sm font-semibold ${(sub.signals?.[k] ?? 0) >= 4 ? 'text-[#0F392B]' : (sub.signals?.[k] ?? 0) >= 2 ? 'text-[#D4AF37]' : 'text-red-500'}`}>{sub.signals?.[k] ?? 0}/6</p>
                </div>
              ))}
            </div>
            {sub.free_text && (
              <div className="mb-3">
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Student's narrative thread</p>
                <p className="text-xs text-foreground/80 leading-relaxed italic bg-white border border-[#E2E2E2] rounded-lg p-3">"{sub.free_text}"</p>
              </div>
            )}
            {sub.ai_analysis && (
              <div>
                <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-wide mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Strategist's Note</p>
                <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-line bg-white border border-[#E2E2E2] rounded-lg p-3">{sub.ai_analysis}</p>
              </div>
            )}
            {!sub.free_text && !sub.ai_analysis && (
              <p className="text-xs text-muted-foreground">No narrative thread or AI note for this submission.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function DiagnosticLeadsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | elite | with-email

  useEffect(() => {
    api.getDiagnosticSubmissions()
      .then(setData)
      .catch(() => setData({ total: 0, elite_count: 0, with_email: 0, submissions: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0F392B]" /></div>;

  const subs = (data?.submissions || []).filter(s => {
    if (filter === 'elite') return (s.recommendation || '').includes('Elite');
    if (filter === 'with-email') return !!s.email;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="diagnostic-leads-tab">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Submissions', value: data?.total ?? 0 },
          { label: 'Elite-Band Leads', value: data?.elite_count ?? 0, gold: true },
          { label: 'With Email', value: data?.with_email ?? 0 },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E2E2E2] p-5" data-testid={`diagnostic-leads-stat-${i}`}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</p>
            <p className={`text-3xl font-semibold mt-1 ${s.gold ? 'text-[#D4AF37]' : 'text-[#0F392B]'}`} style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {[
          { key: 'all', label: 'All' },
          { key: 'elite', label: 'Elite band only' },
          { key: 'with-email', label: 'With email' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filter === f.key ? 'bg-[#0F392B] text-white border-[#0F392B]' : 'bg-white text-muted-foreground border-[#E2E2E2] hover:border-[#0F392B]'}`}
            data-testid={`diagnostic-leads-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card className="border-[#E2E2E2] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="diagnostic-leads-table">
            <thead>
              <tr className="bg-[#F9F9F7] border-b border-[#E2E2E2]">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Date</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Email</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Score</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">Recommendation</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide py-3 px-4">AI Note</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => <SubmissionRow key={s.id} sub={s} />)}
            </tbody>
          </table>
          {subs.length === 0 && (
            <div className="text-center py-12">
              <Target className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No diagnostic submissions {filter !== 'all' ? 'matching this filter' : 'yet'}.</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
